import { NodeSelection, PluginKey } from '@atlaskit/editor-prosemirror/state';
import type { EditorState, Transaction, Selection } from '@atlaskit/editor-prosemirror/state';
import type { Command, NextEditorPlugin } from '@atlaskit/editor-common/types';
import { hasNode } from '@atlaskit/editor-common/utils';
import type { Node as PMNode, Slice } from '@atlaskit/editor-prosemirror/model';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import {
	GapCursorSelection,
	Side,
	atTheBeginningOfDoc,
	atTheEndOfDoc,
} from '@atlaskit/editor-common/selection';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { setSelection } from './actions';

const pluginName = 'legacyContentMacroPlugin';
const pluginKey = new PluginKey<LegacyContentMacroPluginState>(pluginName);

type LegacyContentMacroPluginState = {
	nodeId: string | null | undefined;
	side: Side | null | undefined;
	arrowKeyPressed: string | null | undefined;
	isSelectionAtEdge: boolean | null | undefined;
};

const LEGACY_CONTENT_MACRO_EXTENSION_TYPE = 'com.atlassian.confluence.migration';
const LEGACY_CONTENT_MACRO_EXTENSION_KEY = 'legacy-content';

const isLegacyContentMacroExtension = (extensionNode: PMNode) =>
	extensionNode.type.name === 'extension' &&
	extensionNode.attrs?.extensionType === LEGACY_CONTENT_MACRO_EXTENSION_TYPE &&
	extensionNode.attrs?.extensionKey === LEGACY_CONTENT_MACRO_EXTENSION_KEY;

export type LegacyContentMacroPlugin = NextEditorPlugin<
	typeof pluginName,
	{
		pluginConfiguration: { nodeId?: string } | undefined;
		sharedState: LegacyContentMacroPluginState | undefined;
		actions: {
			setSelection: (selection?: Selection) => Command;
		};
	}
>;

export const legacyContentMacroPlugin: LegacyContentMacroPlugin = ({ config }) => {
	const isInnerEditor = !!config?.nodeId;

	return {
		name: pluginName,
		pmPlugins() {
			return [
				{
					name: pluginName,
					plugin: () => {
						return new SafePlugin<LegacyContentMacroPlugin>({
							key: pluginKey,
							state: {
								init() {
									return {
										nodeId: null,
										side: null,
										arrowKeyPressed: null,
										isSelectionAtEdge: null,
									};
								},
								apply: (tr: Transaction, currentPluginState: LegacyContentMacroPluginState) => {
									const meta = tr.getMeta(pluginKey);

									if (meta) {
										return {
											...currentPluginState,
											...meta,
										};
									}
									return currentPluginState;
								},
							},
							props: {
								handleDOMEvents: {
									keydown(view: EditorView, event: KeyboardEvent) {
										// If the user selects an LCM node and clicks an arrow, update the state with
										// the arrow they clicked and the selected node to use for keyboard navigation
										// logic in the extension-handler
										// We need to track two events to derive the user intention:
										//   1. Arrow key press event when selecting the node, to know whether the user
										//      is coming from the left or right gap cursor
										//   2. Arrow key press event when the node is selected, to know where to enter
										//      the legacy content macro
										// All of these keyboard events are only activated in the outer editor because
										// you can't have selections on the LCM node in the inner editor
										if (
											event instanceof KeyboardEvent &&
											['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key) &&
											!isInnerEditor
										) {
											const { state, dispatch } = view;
											const pluginState = pluginKey.getState(state);

											// When hitting the gap cursor before or after an LCM
											if (
												state.selection instanceof GapCursorSelection &&
												((state.selection.side === Side.RIGHT &&
													state.selection.$from.nodeBefore !== null &&
													isLegacyContentMacroExtension(state.selection.$from.nodeBefore)) ||
													(state.selection.side === Side.LEFT &&
														state.selection.$from.nodeAfter !== null &&
														isLegacyContentMacroExtension(state.selection.$from.nodeAfter)))
											) {
												dispatch(
													state.tr.setMeta(pluginKey, {
														side: state.selection.side,
													}),
												);

												// When hitting the LCM from the left or right
											} else if (
												state.selection instanceof NodeSelection &&
												isLegacyContentMacroExtension(state.selection.node) &&
												// Moving from the left into the LCM
												((event.key === 'ArrowRight' && pluginState?.side === Side.LEFT) ||
													// Moving from the right into the LCM
													(event.key === 'ArrowLeft' && pluginState?.side === Side.RIGHT))
											) {
												dispatch(
													state.tr.setMeta(pluginKey, {
														nodeId: state.selection.node.attrs.localId,
														arrowKeyPressed: event.key,
													}),
												);
												event.preventDefault();

												// When hitting the LCM from below or above
											} else if (
												state.selection instanceof NodeSelection &&
												isLegacyContentMacroExtension(state.selection.node) &&
												['ArrowUp', 'ArrowDown'].includes(event.key)
											) {
												dispatch(
													state.tr.setMeta(pluginKey, {
														nodeId: state.selection.node.attrs.localId,
														// When using the up and down arrow keys intent isn't obvious, so we assume the user wants to enter from the end when coming up and enter from the left when coming down
														side: event.key === 'ArrowUp' ? Side.RIGHT : Side.LEFT,
														arrowKeyPressed: event.key,
													}),
												);
												event.preventDefault();
											}
										}

										// In the inner editor detect when we try exiting the LCM node and trigger
										// selecting the LCM node
										if (
											event instanceof KeyboardEvent &&
											['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key) &&
											isInnerEditor
										) {
											const { state, dispatch } = view;
											if (
												state.selection instanceof GapCursorSelection &&
												// Arrow moving up or left at the initial position of the encapsulating LCM
												(((event.key === 'ArrowUp' || event.key === 'ArrowLeft') &&
													atTheBeginningOfDoc(state)) ||
													// Arrow moving down or right at the end position of the encapsulating LCM
													((event.key === 'ArrowDown' || event.key === 'ArrowRight') &&
														atTheEndOfDoc(state)))
											) {
												dispatch(
													state.tr.setMeta(pluginKey, {
														nodeId: config.nodeId,
														isSelectionAtEdge: true,
													}),
												);
											} else {
												const pluginState = pluginKey.getState(state);
												if (pluginState?.isSelectionAtEdge) {
													dispatch(
														state.tr.setMeta(pluginKey, {
															isSelectionAtEdge: false,
														}),
													);
												}
											}
										}

										return false;
									},
									blur(view: EditorView) {
										// When entering the LCM editor from the outer editor, reset the state
										// to avoid unexpected selection issues
										if (!isInnerEditor) {
											const pluginState = pluginKey.getState(view.state);
											if (pluginState?.arrowKeyPressed) {
												const { tr } = view.state;
												tr.setMeta(pluginKey, {
													nodeId: null,
													side: null,
													arrowKeyPressed: null,
												});
												view.dispatch(tr);
											}
										}
									},
								},
								handleDrop(_view: EditorView, _event: DragEvent, slice: Slice) {
									// Disable drag-and-drop for selections with a legacy content macro extensions
									const sliceHasLegacyContentMacro = hasNode((node) =>
										isLegacyContentMacroExtension(node),
									)(slice);

									return sliceHasLegacyContentMacro;
								},
							},
						});
					},
				},
			];
		},

		actions: {
			setSelection,
		},

		getSharedState(state: EditorState | undefined) {
			if (!state) {
				return undefined;
			}
			const { nodeId, side, arrowKeyPressed, isSelectionAtEdge } = pluginKey.getState(state) ?? {};

			return {
				nodeId,
				side,
				arrowKeyPressed,
				isSelectionAtEdge,
			};
		},
	};
};
